import { getWidgetSession } from '../session/widget-session.js';
import type { WidgetAnalyticsEnvelope } from './build-event-envelope.js';

const ROOT_ONLY_FIELDS = [
  'slug',
  'visitor_id',
  'session_id',
  'company_id',
  'thread_id',
] as const;

type RootOnlyField = (typeof ROOT_ONLY_FIELDS)[number];

/** One event in `POST /api/widget/events` — session fields only on request root. */
export type WidgetAnalyticsEventItem = Omit<
  WidgetAnalyticsEnvelope,
  RootOnlyField
>;

/** Request body for `POST /api/widget/events`. */
export type WidgetAnalyticsEventsRequest = {
  slug: string;
  visitor_id: string;
  session_id: number | null;
  company_id: number | null;
  thread_id: number | null;
  events: WidgetAnalyticsEventItem[];
};

export function resolveAnalyticsSlugSync(
  events?: WidgetAnalyticsEnvelope[],
): string | null {
  const session = getWidgetSession();
  if (session?.slug) return session.slug;

  if (events) {
    for (const event of events) {
      if (event.slug) return event.slug;
    }
  }

  return null;
}

function resolveRootNumericField(
  key: 'session_id' | 'company_id' | 'thread_id',
  events: WidgetAnalyticsEnvelope[],
): number | null {
  const session = getWidgetSession();
  if (session) {
    if (key === 'session_id') return session.sessionId;
    if (key === 'company_id') return session.companyId;
    if (key === 'thread_id') return session.threadId;
  }

  for (let i = events.length - 1; i >= 0; i--) {
    const value = events[i]![key];
    if (value != null) return value;
  }

  return null;
}

function toApiEventItem(
  event: WidgetAnalyticsEnvelope,
): WidgetAnalyticsEventItem {
  const {
    slug: _slug,
    visitor_id: _visitorId,
    session_id: _sessionId,
    company_id: _companyId,
    thread_id: _threadId,
    ...item
  } = event;
  return item;
}

export function buildWidgetAnalyticsEventsBody(
  visitorId: string,
  events: WidgetAnalyticsEnvelope[],
): WidgetAnalyticsEventsRequest | null {
  const slug = resolveAnalyticsSlugSync(events);
  if (!slug) return null;

  return {
    slug,
    visitor_id: visitorId,
    session_id: resolveRootNumericField('session_id', events),
    company_id: resolveRootNumericField('company_id', events),
    thread_id: resolveRootNumericField('thread_id', events),
    events: events.map(toApiEventItem),
  };
}
